import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataGrid } from '@mui/x-data-grid';
import { 
    Box, Typography, Chip, Button, Menu, MenuItem,
    CircularProgress, Alert, Stack, TextField, FormControl, InputLabel, Select, Grid
} from '@mui/material';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { ticketService, configService } from '../api';

const TicketReception = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [tickets, setTickets] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [projects, setProjects] = useState([]);
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const [rowCount, setRowCount] = useState(0);

    // Filter States
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('ALL');
    const [filterPriority, setFilterPriority] = useState('ALL');
    const [filterAgent, setFilterAgent] = useState('ALL');
    const [filterProject, setFilterProject] = useState('ALL');

    useEffect(() => {
        fetchData();
    }, [page, pageSize, searchTerm, filterStatus, filterPriority, filterAgent, filterProject]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                page: page + 1, // DataGrid usa base 0, Django usa base 1
                page_size: pageSize,
                search: searchTerm,
                status: filterStatus,
                priority: filterPriority,
                agent: filterAgent,
                project: filterProject
            };
            
            const ticketRes = await ticketService.getTickets(params);
            const agentRes = await ticketService.getAgents();
            const statusRes = await configService.getStatuses();
            const projectRes = await configService.getProjects();
            
            // Cuando hay paginación, Django devuelve { count, results, ... }
            setTickets(ticketRes.data.results || ticketRes.data);
            setRowCount(ticketRes.data.count || 0);
            setAgents(agentRes.data.results || agentRes.data);
            setStatuses(statusRes.data.results || statusRes.data);
            setProjects(projectRes.data.results || projectRes.data);
        } catch (err) {
            console.error("Failed to fetch data", err);
        } finally {
            setLoading(false);
        }
    };

    const getStatusChip = (key) => {
        const config = statuses.find(s => s.key === key);
        return (
            <Chip 
                label={config ? config.name : key} 
                sx={config ? { bgcolor: config.color, color: '#fff', fontWeight: 'bold' } : {}} 
                size="small" 
            />
        );
    };

    const columns = [
        { 
            field: 'actions', 
            headerName: 'Acciones', 
            width: 100,
            sortable: false,
            renderCell: (params) => (
                <Button 
                    variant="contained" 
                    size="small" 
                    onClick={() => navigate(`/tickets/${params.row.id}`)}
                >
                    Ver
                </Button>
            )
        },
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'project_name', headerName: 'Proyecto', width: 150 },
        { field: 'title', headerName: 'Asunto', flex: 1, minWidth: 200 },
        { 
            field: 'status', 
            headerName: 'Estado', 
            width: 130,
            renderCell: (params) => getStatusChip(params.value)
        },
        { 
            field: 'priority', 
            headerName: 'Prioridad', 
            width: 120,
            renderCell: (params) => (
                <Chip 
                    label={params.value} 
                    color={params.value === 'HIGH' ? 'error' : params.value === 'MEDIUM' ? 'warning' : 'success'} 
                    size="small" 
                />
            )
        },
        { field: 'submitter_email', headerName: 'Remitente', width: 180 },
        { field: 'assigned_to_email', headerName: 'Asignado a', width: 180, 
          valueGetter: (params) => params.row.assigned_to_email || 'Sin Asignar' },
        { 
            field: 'attachments', 
            headerName: 'Archivos', 
            width: 90,
            valueGetter: (params) => params.row.attachments?.length || 0,
            renderCell: (params) => params.value > 0 ? <Chip size="small" label={`${params.value} 📎`} /> : '-'
        },
        { 
            field: 'created_at', 
            headerName: 'Creado', 
            width: 180,
            valueFormatter: (params) => new Date(params.value).toLocaleString()
        },
        { 
            field: 'expected_resolution_date', 
            headerName: 'Límite SLA', 
            width: 180,
            valueFormatter: (params) => new Date(params.value).toLocaleString()
        },
    ];

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ height: 600, width: '100%', p: 3 }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight="bold">Recepción de Tickets</Typography>
            </Box>

            {/* Search and Advanced Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Search title or email..."
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={filterStatus}
                            label="Status"
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <MenuItem value="ALL">All Statuses</MenuItem>
                            {statuses.map(s => (
                                <MenuItem key={s.id} value={s.key}>{s.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Priority</InputLabel>
                        <Select
                            value={filterPriority}
                            label="Priority"
                            onChange={(e) => setFilterPriority(e.target.value)}
                        >
                            <MenuItem value="ALL">All Priorities</MenuItem>
                            <MenuItem value="HIGH">High</MenuItem>
                            <MenuItem value="MEDIUM">Medium</MenuItem>
                            <MenuItem value="LOW">Low</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Proyecto</InputLabel>
                        <Select
                            value={filterProject}
                            label="Proyecto"
                            onChange={(e) => setFilterProject(e.target.value)}
                        >
                            <MenuItem value="ALL">Todos los Proyectos</MenuItem>
                            {projects.map(p => (
                                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Agent</InputLabel>
                        <Select
                            value={filterAgent}
                            label="Agent"
                            onChange={(e) => setFilterAgent(e.target.value)}
                        >
                            <MenuItem value="ALL">All Agents</MenuItem>
                            <MenuItem value="UNASSIGNED">Unassigned</MenuItem>
                            {agents.map(agent => (
                                <MenuItem key={agent.id} value={agent.id}>{agent.email}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                    <Button 
                        fullWidth
                        variant="outlined" 
                        color="secondary"
                        startIcon={<RestartAltIcon />}
                        onClick={() => {
                            setSearchTerm('');
                            setFilterStatus('ALL');
                            setFilterPriority('ALL');
                            setFilterAgent('ALL');
                            setFilterProject('ALL');
                        }}
                    >
                        {t('reset_filters')}
                    </Button>
                </Grid>
            </Grid>

            <DataGrid
                rows={tickets}
                columns={columns}
                pagination
                paginationMode="server"
                rowCount={rowCount}
                pageSize={pageSize}
                page={page}
                onPageChange={(newPage) => setPage(newPage)}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[10, 25, 50, 100]}
                disableSelectionOnClick
                initialState={{
                    sorting: { sortModel: [{ field: 'created_at', sort: 'desc' }] },
                }}
            />
        </Box>
    );
};

export default TicketReception;